import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '../../../../app/utils/formatters';
import { Payment } from '../../../../app/data/models/payments/payment';

interface RefundDialogProps {
  open: boolean;
  onClose: () => void;
  selectedPayment: Payment | null;
  refundAmount: string;
  refundReason: string;
  printReceipt: boolean;
  processingAction: boolean;
  onRefundAmountChange: (value: string) => void;
  onRefundReasonChange: (value: string) => void;
  onPrintReceiptChange: (checked: boolean) => void;
  onRefundSubmit: () => void;
}

const RefundDialog: React.FC<RefundDialogProps> = ({
  open,
  onClose,
  selectedPayment,
  refundAmount,
  refundReason,
  printReceipt,
  processingAction,
  onRefundAmountChange,
  onRefundReasonChange,
  onPrintReceiptChange,
  onRefundSubmit,
}) => {
  const { t } = useTranslation();

  if (!selectedPayment) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('biller.refund-payment')}</DialogTitle>
      <DialogContent>
        <Box sx={{ p: 1 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {t('biller.original-payment')}:{' '}
            {formatCurrency(selectedPayment.amount)}
          </Typography>
          <TextField
            label={t('biller.refund-amount')}
            type="number"
            fullWidth
            value={refundAmount}
            onChange={(e) => onRefundAmountChange(e.target.value)}
            inputProps={{ min: 0, max: selectedPayment.amount, step: 0.01 }}
            sx={{ mb: 2 }}
          />
          <TextField
            label={t('biller.refund-reason')}
            fullWidth
            value={refundReason}
            onChange={(e) => onRefundReasonChange(e.target.value)}
            sx={{ mb: 2 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={printReceipt}
                onChange={(e) => onPrintReceiptChange(e.target.checked)}
              />
            }
            label={t('biller.print-receipt')}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={processingAction}>
          {t('common.cancel')}
        </Button>
        <Button
          onClick={onRefundSubmit}
          color="primary"
          variant="contained"
          disabled={
            processingAction ||
            !refundAmount ||
            Number(refundAmount) <= 0 ||
            Number(refundAmount) > selectedPayment.amount
          }
        >
          {processingAction ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t('biller.refund')
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RefundDialog;
